import { Divider, Radio } from "antd";
import React, { useEffect, useRef, useState } from 'react';
import { renderToString } from "react-dom/server";
import { useSelector } from "react-redux";
import { ReactComponent as treatmentIcon } from "../../../../../assets/icon/add_2.svg";
import { ReactComponent as eventIcon } from "../../../../../assets/icon/event_operation.svg";
import { ReactComponent as transportIcon } from "../../../../../assets/icon/fish_1.svg";
import { ReactComponent as harvestIcon } from "../../../../../assets/icon/harvest.svg";
import { ReactComponent as serviceIcon } from "../../../../../assets/icon/service_operation.svg";
import { ICON_NAME } from "../../../../../components/Icon";
import LoadingBox from "../../../../../components/LoadingBox";
import { ChartProvider } from "../../../../../contexts/ChartContext";
import { callEventSiteDetail, useSiteDetail } from "../../../../../contexts/SiteDetailContext";
import { datetime } from "../../../../../util/library/datetime";
import { selectOpMode } from "../../../../../util/store/selectors";
import { operationType } from "../../../../../util/varibles/constants";
import { cloneObj, firstLetter } from "../../../../../util/varibles/global";
import { TOperation } from "../../../../../util/varibles/interface";
import { getTimeByOpMode } from "../../../../PlanOperationPage/util/function_operation/constants";
import { addLiceThreshold, KeyChart, propertiesOfChart, UnitOfChart } from "../constants";
import styles from "../style.module.scss";
import DrawChart from "./DrawChart";
import Legends from "./Legends";
import Operations from "./Operations";


const img = {
    [ICON_NAME.HARVEST]: harvestIcon,
    [ICON_NAME.TREATMENT]: treatmentIcon,
    [ICON_NAME.TRANSPORT]: transportIcon,
    [ICON_NAME.EVENT]: eventIcon,
    [ICON_NAME.SERVICE]: serviceIcon
}

const Year: React.FC = () => {
    const [years] = useState((() => {
        const year = datetime().year;
        const values = [];
        for (let i = 2; i >= 0; i--) {
            values.push(`${year - i}`)
        }
        return values
    })());
    const [year, set] = useSiteDetail(state => state.year);
    const [loadingSite] = useSiteDetail(state => state.loadingSite);
    const [loadingChart] = useSiteDetail(state => state.loadingChart);

    const handleChange = (value: string) => {
        set({ year: value })
    }

    return <Radio.Group
        disabled={loadingSite || loadingChart}
        onChange={({ target }) => handleChange(target.value)}
        value={year}
    >
        {years.map(value => <Radio className="text-[15px]" key={value} value={value}>{value}</Radio>)}
    </Radio.Group>;
};

const Loading: React.FC = () => {
    const [loading] = useSiteDetail(state => state.loadingChart);
    return <>{loading && <LoadingBox loading={loading} level='bg' />}</>
}

const convertData = ([from, to]: number[], type: any) => {
    const result = [];
    const max = from === to ? from + 1 : to
    for (let i = from; i <= max; i++) {
        result.push([`W${i}`, type]);
    }
    return result;
}

const Chart: React.FC = () => {
    const opMode = useSelector(selectOpMode);
    const [remove, setRemove] = useState<KeyChart[]>([]);
    const [source, set] = useSiteDetail(state => state.source);
    const [chart] = useSiteDetail(state => state.chart)
    const [year] = useSiteDetail(state => state.year);
    const [unit] = useSiteDetail(state => state.unit);
    const [site] = useSiteDetail(state => state.site);
    const getChart = useRef(callEventSiteDetail().getChart);

    const [{ opsVisible, opsChart, rowMax }, setOpsVisible] = useState<{
        opsVisible: TOperation[],
        opsChart: any,
        rowMax: number,
    }>({ opsVisible: [], opsChart: [], rowMax: 0 });

    useEffect(() => {
        const operations = site?.operations || [];
        let ops;
        if (unit) {
            const { id } = unit;
            ops = operations.reduce((rs: TOperation[], item) => {
                const index = item.sub_operations.findIndex(sub => sub.id === id);
                if (index >= 0) {
                    const sub_operations = cloneObj(item.sub_operations);
                    sub_operations[index].selected = true;
                    rs.push({ ...item, sub_operations });
                }

                return rs
            }, [])
        } else {
            ops = operations;
        }
        let yCount: { [id: string]: number } = {};
        const color = document.body.classList.contains('theme-dark') ? '#D2D6DA' : '#2D3939';
        const count: { [week: string]: number } = {};
        const stateOps = {
            opsVisible: ops,
            opsChart: ops.map(item => {
                const { processes = [], operation_type } = item;
                const { icon } = operationType[operation_type];
                const time = getTimeByOpMode[opMode](processes[0]);
                const x = datetime(time).week - 1;
                const y = yCount[x] || 0;
                yCount[x] = y + 1;
                const { [icon!]: Symbol }: any = img;
                const svgEl = new DOMParser().parseFromString(renderToString(<Symbol />), 'application/xml')
                const pathEL = svgEl.getElementsByTagName('path')[0];
                pathEL.setAttribute('style', `fill: ${color}`);
                const s = new XMLSerializer().serializeToString(svgEl);
                const path = window.btoa(s);
                pathEL.setAttribute('style', `fill:#2D3939`);
                const week = datetime(getTimeByOpMode[opMode](processes[0])).week;
                count[week] = (count[week] || 0) + 1;

                return {
                    name: `#${item.operation_code}`,
                    type: 'scatter',
                    data: [[x, y, item]],
                    symbolSize: 15,
                    symbolKeepAspect: true,
                    symbol: 'image://data:image/svg+xml;base64,' + path,
                    itemStyle: { color },
                    tooltip: {
                        formatter: function (params: any) {
                            const data = params.value[2] as TOperation;
                            const result = [
                                `W${x + 1}`,
                                `<div class='font-bold flex items-center gap-1 text-[15px]'>
                                    ${new XMLSerializer().serializeToString(svgEl)}
                                    <span class="colorMain">#${data.operation_code}</span> 
                                </div>`
                            ]
                            const starvingDates = data.sub_operations.filter(item => item.starving_days);
                            if (starvingDates.length > 0) {
                                result.push([
                                    `<div class="colorMain">`,
                                    `<span class="text-comment font-bold text-[13px]">Starving Days: </span>`,
                                    `<span class="text-[15px]">`,
                                    starvingDates.map(item => `${item.unit_id} - ${item.starving_days}`).join(', '),
                                    `</span></div>`
                                ].join(' '))

                            }
                            return result.join(' ')
                        },
                    }
                }
            }),
        }
        setOpsVisible({ ...stateOps, rowMax: Math.max(...Object.keys(count).map(key => count[key])) });
    }, [unit, site?.operations, opMode]);

    useEffect(() => {
        getChart.current();
    }, [site, unit, year]);

    useEffect(() => {
        if (Object.keys(source).length > 0) {
            const yLine: { [id: string]: string } = { [UnitOfChart.Lice]: '0' };
            const yArea: { [id: string]: string } = {};
            const { count, line, area } = source;
            const ids = new Set(remove);
            const chart = {
                line: Object.keys(line).reduce((rs: any, key) => {
                    if (ids.has(key as KeyChart)) {
                        return rs;
                    }
                    const { name, unit, color, lineColor, zlevel } = propertiesOfChart[key] || {};
                    yLine[unit || ''] = `${yLine[unit || ''] || Object.keys(yLine).length}`;
                    const { [key]: statistics }: any = line;
                    return [...rs, {
                        name: name ? name : firstLetter(key.replace(/_/g, ' ')),
                        type: 'line',
                        data: unit ? statistics.map((item: number) => item ? (Math.round(item * 1000) / 1000) : item) : [],
                        yAxisIndex: yLine[unit || ''],
                        symbol: !lineColor ? 'emptyCircle' : 'none',
                        itemStyle: {
                            color: color,
                        },
                        lineStyle: {
                            color: lineColor || color
                        },
                        zlevel,
                        tooltip: {
                            valueFormatter: (value = '-') => value === '-' ? value : [value, unit].join(' ')
                        }
                    }];
                }, []),
                area: Object.keys(area).reduce((rs: any, key) => {
                    if (ids.has(key as KeyChart) || area[key].length === 0) {
                        return rs;
                    }
                    const {
                        name = key,
                        color,
                        lineColor
                    } = propertiesOfChart[key] || propertiesOfChart[KeyChart.DiseaseLocal];
                    const start = datetime().set({ year, week: 1 }).startOf('week').time;
                    const weeksInYear = datetime().set({ year }).weeksInYear;
                    const finish = datetime().set({ year, week: weeksInYear }).endOf('week').time;

                    const { type = 'DiseaseLocal' } = propertiesOfChart[key] || {}
                    yArea[type] = `${yArea[type] || Object.keys(yArea).length}`;
                    let { [key]: statistics }: any = area;
                    statistics.forEach((item: any) => {
                        const { from, to } = item;
                        rs.push({
                            name,
                            type: 'line',
                            symbol: 'none',
                            data: convertData([datetime(Math.max(from, start)).week, datetime(Math.min(to || Date.now(), finish)).week - 1], Number(yArea[type])),
                            yAxisIndex: 0,
                            itemStyle: { color },
                            lineStyle: {
                                width: 10,
                                color: lineColor || color
                            },
                            tooltip: {
                                valueFormatter: () => `${datetime(from).format('MMM DD, YYYY')} - ${to ? datetime(to).format('MMM DD, YYYY') : 'Now'}`
                            },
                        });
                    })
                    return rs
                }, [])
            }
            chart.line.push(addLiceThreshold(count, yLine));
            set({ chart: { yLine, yArea, count, ...chart } })
        }
    }, [remove, set, source, year]);

    return <>
        <div className="flex items-center justify-between">
            <div className={[styles.lineName, 'mb-0'].join(' ')}>
                {unit ? <>
                    <span className='link' onClick={() => set({ unit: undefined })}>Back to site</span>
                    <Divider orientation="vertical" />
                    <span>{unit.unit_id}’s chart & operations</span>
                </> : <span>
                    Sites chart & operations
                </span>}
            </div>
            <Year />
        </div>
        <ChartProvider>
            <div className="relative">
                <DrawChart className={styles.chart} ops={opsChart} chart={chart} rowMax={rowMax} />
                <Legends data={source} remove={remove} onChange={setRemove} />
                <Loading />
            </div>
        </ChartProvider>
        <Operations ops={opsVisible} />
    </>;
};

export default Chart;
